/**
 * Player Relationships — Player Platform capability (not Arena-owned).
 * Generic entity; Phase 1 exposes Friend only. Future types reuse the same shape.
 */

export const RELATIONSHIP_TYPES = Object.freeze({
  FRIEND: "friend",
  // Future (do not expose in UI yet):
  // COACH: "coach",
  // STUDENT: "student",
  // PARENT: "parent",
  // CHILD: "child",
  // FAVOURITE_PARTNER: "favourite_partner",
  // BLOCKED: "blocked",
  // FOLLOWER: "follower",
});

export const RELATIONSHIP_STATUSES = Object.freeze({
  PENDING: "pending",
  ACCEPTED: "accepted",
  DECLINED: "declined",
  CANCELLED: "cancelled",
  BLOCKED: "blocked",
});

export const RELATIONSHIP_STATUS_LABELS = Object.freeze({
  pending: "Pending",
  accepted: "Friends",
  declined: "Declined",
  cancelled: "Cancelled",
  blocked: "Blocked",
});

/** Search rank: friends → pending → others */
export const RELATIONSHIP_SEARCH_RANK = Object.freeze({
  friend: 0,
  pending: 1,
  none: 2,
});

export function generateRelationshipId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return `prel_${crypto.randomUUID().replace(/-/g, "").slice(0, 20)}`;
  }
  return `prel_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Deterministic pair key so friend edges are unique regardless of direction.
 * @param {string} a
 * @param {string} b
 * @param {string} [type]
 */
export function buildRelationshipPairKey(a, b, type = RELATIONSHIP_TYPES.FRIEND) {
  const left = String(a || "").trim();
  const right = String(b || "").trim();
  const [x, y] = [left, right].sort();
  return `${type}:${x}__${y}`;
}

/**
 * Stable document id from pair key (rules-safe get without list).
 * @param {string} pairKey
 */
export function relationshipIdFromPairKey(pairKey) {
  const raw = String(pairKey || "")
    .replace(/[^a-zA-Z0-9:_-]/g, "_")
    .slice(0, 140);
  return `prel_${raw}`;
}

/**
 * @param {object} input
 */
export function buildPlayerRelationshipDocument(input) {
  const now = Date.now();
  const fromPlayerId = String(input.fromPlayerId || input.playerId || "").trim();
  const toPlayerId = String(input.toPlayerId || input.relatedPlayerId || "").trim();
  const relationshipType =
    input.relationshipType || RELATIONSHIP_TYPES.FRIEND;
  const pairKey =
    input.pairKey ||
    buildRelationshipPairKey(fromPlayerId, toPlayerId, relationshipType);
  return {
    relationshipId:
      input.relationshipId || relationshipIdFromPairKey(pairKey),
    /** Spec aliases — requester */
    playerId: fromPlayerId,
    /** Spec aliases — target */
    relatedPlayerId: toPlayerId,
    fromPlayerId,
    toPlayerId,
    fromUserId: input.fromUserId || null,
    toUserId: input.toUserId || null,
    fromPlayerName: input.fromPlayerName || null,
    toPlayerName: input.toPlayerName || null,
    memberPlayerIds: [fromPlayerId, toPlayerId].filter(Boolean),
    memberUserIds: [input.fromUserId, input.toUserId].filter(Boolean),
    pairKey,
    relationshipType,
    status: RELATIONSHIP_STATUSES.PENDING,
    createdBy: input.createdBy,
    createdAtMs: now,
    updatedAtMs: now,
    acceptedAtMs: null,
    declinedAtMs: null,
  };
}

/**
 * @param {object | null | undefined} rel
 * @param {string} viewerPlayerId
 */
export function otherPlayerIdInRelationship(rel, viewerPlayerId) {
  if (!rel || !viewerPlayerId) return null;
  if (rel.fromPlayerId === viewerPlayerId) return rel.toPlayerId;
  if (rel.toPlayerId === viewerPlayerId) return rel.fromPlayerId;
  if (rel.playerId === viewerPlayerId) return rel.relatedPlayerId;
  if (rel.relatedPlayerId === viewerPlayerId) return rel.playerId;
  const members = (rel.memberPlayerIds || []).filter(Boolean);
  if (members.includes(viewerPlayerId)) {
    return members.find((id) => id !== viewerPlayerId) || null;
  }
  return null;
}

/**
 * Viewer-facing friend search badge.
 * @param {object | null | undefined} rel
 * @param {string} viewerPlayerId
 * @returns {'friend'|'pending_sent'|'pending_received'|'none'}
 */
export function resolveFriendSearchState(rel, viewerPlayerId) {
  if (!rel || !viewerPlayerId) return "none";
  if (rel.status === RELATIONSHIP_STATUSES.ACCEPTED) return "friend";
  if (rel.status === RELATIONSHIP_STATUSES.PENDING) {
    if (rel.fromPlayerId === viewerPlayerId) return "pending_sent";
    if (rel.toPlayerId === viewerPlayerId) return "pending_received";
    return "pending_sent";
  }
  return "none";
}

/**
 * @param {'friend'|'pending_sent'|'pending_received'|'none'|string} state
 */
export function friendSearchRank(state) {
  if (state === "friend") return RELATIONSHIP_SEARCH_RANK.friend;
  if (state === "pending_sent" || state === "pending_received") {
    return RELATIONSHIP_SEARCH_RANK.pending;
  }
  return RELATIONSHIP_SEARCH_RANK.none;
}

/**
 * Sort directory rows: friends → pending → others, then name.
 * @param {object[]} items
 * @param {(item: object) => string} getState
 */
export function sortPlayersByRelationshipPriority(items, getState) {
  return [...(items || [])].sort((a, b) => {
    const ra = friendSearchRank(getState(a));
    const rb = friendSearchRank(getState(b));
    if (ra !== rb) return ra - rb;
    const na = String(a.displayName || "").toLowerCase();
    const nb = String(b.displayName || "").toLowerCase();
    return na.localeCompare(nb);
  });
}
